import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useToast,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Flex,
  Text,
  FormErrorMessage,
  Select,
} from "@chakra-ui/react";
import { createProject, updateProject, getFaucetTokens } from "../../api";
import {
  createProjectWithEntrypoint,
  getProjectIdFromUserOperation,
} from "../../api/createProject";
import { getUSDCBalance } from "../../api/utils/token";
import { getNetworkEnvironment } from "../../api/config/networks";
import LoadingStepper from "../LoadingStepper";
import FormHelpPopover from "../FormHelpPopover";
import { encryptWithPublicKey, determineFrameworkType } from "../../util";
import { LOADING_STEPS_CREATE_PROJECT } from "../../constants";
import { useFeatureFlags } from "../../context/featureFlagsContext";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";

const initialState = {
  name: "",
  email: "",
  buildCommands: {
    buildCommand: "",
    buildDir: "",
    environmentVariables: "",
    outputDir: "",
    packageInstallCommand: "",
  },
  hostedProjectUrl: "",
  projectType: "",
  projectInfo: {
    filePath: "",
  } as ProjectInfoManual,
} as ProjectObjectToCreate;

const ProjectConfigManual = ({
  isEditing = false,
  projectData: projectDataInitial,
}: {
  isEditing?: boolean;
  projectData?: ProjectObject;
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [projectCreating, setProjectCreating] = useState(false);
  const [projectData, setProjectData] = useState(
    projectDataInitial || initialState,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [framework, setFrameworkType] = useState("");

  const { featureFlags } = useFeatureFlags();
  const isFrameworkEnabled = featureFlags?.IS_FRAMEWORK_ENABLED;

  const { primaryWallet } = useDynamicContext();

  useEffect(() => {
    if (!projectData || framework) return;
    const frameworkType = determineFrameworkType(
      projectData,
      isFrameworkEnabled || false,
    );
    setFrameworkType(frameworkType);
  }, [isFrameworkEnabled, projectData, framework]);

  // Refs for scrolling to error fields
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (projectData.buildCommands && name in projectData.buildCommands) {
      setProjectData((prev) => ({
        ...prev,
        buildCommands: { ...prev.buildCommands, [name]: value },
      }));
    } else if (projectData.projectInfo && name in projectData.projectInfo) {
      setProjectData((prev) => ({
        ...prev,
        projectInfo: { ...prev.projectInfo, [name]: value },
      }));
    } else {
      setProjectData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear the error for the field being edited
    setFormErrors((prev) => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { name, email, buildCommands } = projectData;

    const errors: Record<string, string> = {};
    if (!name) errors.name = "Project name is required";
    if (!email) errors.email = "Contact email is required";
    if (!framework) errors.framework = "Framework is required";

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast({
        title: "Missing information",
        description: "Please fill all required fields before submitting.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });

      // Scroll to the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      const refs = {
        name: nameRef,
        email: emailRef,
      };

      const refToScroll = refs[firstErrorField as keyof typeof refs];
      if (refToScroll && refToScroll.current) {
        refToScroll.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return;
    }

    try {
      let response = null;
      const encryptedEnvironmentVariables = encryptWithPublicKey(
        buildCommands.environmentVariables,
      );
      const payload = {
        name,
        email,
        buildCommands: {
          ...buildCommands,
          environmentVariables: encryptedEnvironmentVariables,
        },
        projectType: "upload",
        projectInfo: {
          ...projectData.projectInfo,
          framework: framework === "nextjs-static" ? "static" : framework,
        },
      };
      setProjectCreating(true);
      if (isEditing) {
        response = await updateProject(
          (projectData as ProjectObject).id,
          payload,
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update project.");
        }

        toast({
          title: "Project Updated!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      let projectId = null;
      if (primaryWallet && getNetworkEnvironment() !== "development") {
        // create transaction to the entrypoint contract on the client for dynamic users

        // Project creation parameters
        // TODO: parameterize these
        const nodesToReserve = 1;
        const escrowAmount = 100000000000000000;
        const fundingWallet = primaryWallet.address;

        // check if the wallet is already funded
        const walletUSDCBalance = await getUSDCBalance(fundingWallet);
        if (walletUSDCBalance < escrowAmount) {
          // use the CLI to call the faucet to get token for the project in this chain and wallet
          const faucetResponse = await getFaucetTokens(fundingWallet);
        }

        projectId = await createProjectWithEntrypoint({
          primaryWallet: primaryWallet,
          projectData: payload,
          fundingWallet: fundingWallet,
          nodesToReserve: nodesToReserve,
          escrowAmount: escrowAmount,
          slot: { last: true, next: true },
          nodeIds: [],
          nodePrices: [],
        });
      } else {
        // create project via CLI to handle existing non-dynamic users
        const response = await createProject(payload);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create project.");
        }
        const data = await response.json();
        projectId = data.id;
      }

      toast({
        title: "Project Created",
        description: "Project successfully created. You can now upload files.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate(`/my-projects/${projectId}/deploy`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create project";

      toast({
        title: "Failed to create project",
        description: message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setProjectCreating(false);
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit} noValidate>
        {!isEditing && (
          <Flex
            marginTop={{ base: "20px", md: "40px" }}
            border="2x solid #eeeeee"
            width="100%"
          >
            <Flex
              backgroundColor="gray.50"
              borderRadius="5px"
              border="2px solid #eeeeee"
              width="100%"
            >
              <VStack
                spacing={{ base: 4, md: 6 }}
                align="center"
                m={{ base: 3, md: 5 }}
                width="100%"
              >
                <FormControl
                  isInvalid={!!formErrors.name}
                  isDisabled={isEditing}
                >
                  <FormLabel
                    display="inline-block"
                    color={formErrors.name ? "red.500" : "inherit"}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Project Name
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    ref={nameRef}
                    backgroundColor="white"
                    name="name"
                    value={projectData.name}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                    fontSize={{ base: "sm", md: "md" }}
                    height={{ base: "32px", md: "40px" }}
                  />
                  <FormErrorMessage fontSize={{ base: "xs", md: "sm" }}>
                    {formErrors.name}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!formErrors.email}
                  isDisabled={isEditing}
                >
                  <FormLabel
                    display="inline-block"
                    color={formErrors.email ? "red.500" : "inherit"}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Contact Email
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  </FormLabel>
                  <FormHelpPopover text="This email serves as a contact for the project in case node operators want to communicate with you or users want to get in touch to verify authenticity." />
                  <Input
                    ref={emailRef}
                    backgroundColor="white"
                    name="email"
                    value={projectData.email}
                    onChange={handleInputChange}
                    placeholder="Enter contact email"
                    fontSize={{ base: "sm", md: "md" }}
                    height={{ base: "32px", md: "40px" }}
                  />
                  <FormErrorMessage fontSize={{ base: "xs", md: "sm" }}>
                    {formErrors.email}
                  </FormErrorMessage>
                </FormControl>
                {isFrameworkEnabled && (
                  <FormControl isInvalid={!!formErrors.framework}>
                    <FormLabel
                      display="inline-block"
                      color={formErrors.framework ? "red.500" : "inherit"}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      Framework
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    </FormLabel>
                    <FormHelpPopover text="Selecting a framework is necessary for deploying the application." />
                    <Select
                      placeholder="Select framework"
                      value={framework}
                      onChange={(e) => setFrameworkType(e.target.value)}
                      fontSize={{ base: "sm", md: "md" }}
                      height={{ base: "32px", md: "40px" }}
                      _placeholder={{ fontSize: { base: "sm", md: "md" } }}
                      backgroundColor="white"
                    >
                      <option value="nextjs">Next.js</option>
                      <option value="nextjs-static">
                        NextJS (Static export)
                      </option>
                      <option value="static">Static Site / ReactJS</option>
                    </Select>
                    <FormErrorMessage fontSize={{ base: "xs", md: "sm" }}>
                      {formErrors.framework}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </VStack>
            </Flex>
          </Flex>
        )}
        <Button
          type="submit"
          variant="primary"
          mt={{ base: 3, md: 4 }}
          width="100%"
          isLoading={projectCreating}
          fontSize={{ base: "sm", md: "md" }}
        >
          {isEditing ? "Update Project" : "Create Project"}
        </Button>
      </form>
      {projectCreating && !isEditing && (
        <LoadingStepper steps={LOADING_STEPS_CREATE_PROJECT} />
      )}
    </Box>
  );
};

export default ProjectConfigManual;
